import { useState, useEffect } from 'react';
import { Plus } from 'lucide-react';
import { Container } from '@/components/layout/Container';
import { WeeklyCalendarView } from '@/components/calendar/WeeklyCalendarView';
import { ScheduleCreationModal } from '@/components/calendar/ScheduleCreationModal';
import { ScheduleOptimizationModal } from '@/components/calendar/ScheduleOptimizationModal';
import { CalendarEvent, ListSchedule } from '@/types/calendar';
import { useListsStore } from '@/store/listsStore';
import { useGoalsStore } from '@/store/goalsStore';
import { useAspirationsStore } from '@/store/aspirationsStore';
import { useNavigationStore } from '@/store/navigationStore';
import { useNavigate } from 'react-router-dom';
import { aiService } from '@/services/aiService';

export function Calendar() {
  const navigate = useNavigate();
  const { initializeLists } = useListsStore();
  const { initializeGoals } = useGoalsStore();
  const { initializeAspirations } = useAspirationsStore();
  const { setPageInfo } = useNavigationStore();
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showOptimizationModal, setShowOptimizationModal] = useState(false);
  const [initialListId, setInitialListId] = useState<string | undefined>();
  const [initialDate, setInitialDate] = useState<Date | undefined>();
  const [optimizationKey, setOptimizationKey] = useState(0);

  // Set page info and action on mount
  useEffect(() => {
    setPageInfo('Calendar', {
      icon: Plus,
      label: 'Schedule',
      onClick: () => handleCreateSchedule()
    });
  }, [setPageInfo]);

  useEffect(() => {
    // Initialize all stores to ensure data is available
    initializeLists();
    initializeGoals();
    initializeAspirations();
  }, [initializeLists, initializeGoals, initializeAspirations]);

  const handleEventClick = (event: CalendarEvent) => {
    // Navigate to the list view for this event, passing the event's datetime context
    // Include time to support multiple scheduled events of the same list on the same day
    const eventDateTime = event.startTime.toISOString(); // Full ISO string with time
    navigate(`/list-items/${event.listId}?datetime=${encodeURIComponent(eventDateTime)}`, {
      state: { from: 'calendar' }
    });
  };

  const handleCreateSchedule = (listId?: string, date?: Date) => {
    setInitialListId(listId);
    setInitialDate(date);
    setShowScheduleModal(true);
  };

  const handleOptimizeWeek = () => {
    setShowOptimizationModal(true);
  };

  const handleScheduleCreated = (schedule: ListSchedule) => {
    // Optionally refresh the calendar view or show a success message
    console.log('Schedule created:', schedule);
    setShowScheduleModal(false);
  };

  const handleOptimizationApplied = () => {
    // Force re-render of WeeklyCalendarView by updating optimization key
    setOptimizationKey(prev => prev + 1);
  };

  return (
    <Container size="full" className="py-6">
      <div className="space-y-6">
        {/* Weekly Calendar View */}
        <WeeklyCalendarView
          key={optimizationKey} // Force re-render when optimization is applied
          onEventClick={handleEventClick}
          onCreateSchedule={handleCreateSchedule}
          onOptimizeWeek={aiService.isConfigured() ? handleOptimizeWeek : undefined}
        />
      </div>
      
      <ScheduleCreationModal
        isOpen={showScheduleModal}
        onClose={() => setShowScheduleModal(false)}
        onScheduleCreated={handleScheduleCreated}
        initialListId={initialListId}
        initialDate={initialDate}
      />

      <ScheduleOptimizationModal
        isOpen={showOptimizationModal}
        onClose={() => setShowOptimizationModal(false)}
        onOptimizationApplied={handleOptimizationApplied}
      />
    </Container>
  );
}